import React, {useCallback, useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ProfileKey} from '@findhotel/sapi'
import {SearchBox as DesktopSearchBox} from 'components/Accommodation/AccommodationView/Desktop/SearchBox'
import {useSyncFilters} from 'components/Accommodation/AccommodationView/Desktop/useSyncFilters'
import {useSrpQueryString} from 'components/Accommodation/BackToSearchButton/helpers'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {OfferCheckContextProvider} from 'components/Accommodation/Rooms/OfferCheckContext'
import {InitializeCallToBook} from 'components/CallToBook/InitializeCallToBook'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import LanguageMismatchOverlay from 'components/LanguageMismatchOverlay/LanguageMismatchOverlay'
import Layout from 'components/Layouts'
import TrackPageView from 'components/TrackPageView'
import {useConditionalEffect} from 'hooks/useConditionalEffect'
import {useGetHotelDetails} from 'hooks/useGetHotelDetails'
import {useHotelSlug} from 'hooks/useHotelSlug'
import {useInjectSrpHistory} from 'hooks/useInjectSrpHistory'
import {useIsSrpSearchSeoFlow} from 'hooks/useIsSrpSearchSeoFlow'
import {useSyncStoreWithUrl} from 'hooks/useSyncStoreWithUrl'
import {
  clearRoomsTopOfferLock,
  clearSrpEntryFilters,
  resetAccommodationPageState,
  setSrpEntryFilters
} from 'modules/accommodation/slice'
import {getSapiMetaOptions} from 'modules/meta/selectors'
import {SapiProvider} from 'modules/sapiSearch/SapiProvider'
import {getHotelResult} from 'modules/searchApi/selectors'
import dynamic from 'next/dynamic'
import {getAutoRefreshInterval} from 'utils/autoRefresh'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {makeErrorForwardingDynamicLoader} from '@vio/core/src/_web/utils/next/errorForwardingDynamicLoader'
import InjectPageDataId from '@vio/core/src/analytics/components/InjectPageDataId'
import {
  TeamContextProvider,
  useDispatchTeamEvent
} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  HotelContext,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {getDataFile} from '@vio/core/src/experiments/modules/selectors'
import {collectSapiVariationsParam} from '@vio/core/src/experiments/sapi'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {useTrackPriceRefreshTimeout} from '@vio/shared/src/booking/hooks/useTrackPriceRefreshTimeout'

import {SeoAPStructuredData} from '../../components/SeoSRP/SeoAPStructuredData'
import {RoomPriceStructuredData} from '../../components/StructuredDataMarkup'
import {convertHotelSlugToHotelId} from './convertHotelSlugToHotelId'
import {RoomsPreheatTracking} from './RoomsPreheatTracking'
import {useRedirectHome} from './useRedirectHome'

const Mobile = dynamic(
  async () =>
    import(
      /* webpackChunkName: "accommodationMobile" */
      './Mobile'
    ),
  {
    ssr: true,
    loading: makeErrorForwardingDynamicLoader()
  }
)
Mobile.displayName = 'AccommodationMobileLoadable'

const Desktop = dynamic(
  async () =>
    import(
      /* webpackChunkName: "accommodationDesktop" */
      './Desktop'
    ),
  {
    ssr: true,
    loading: makeErrorForwardingDynamicLoader()
  }
)
Desktop.displayName = 'AccommodationDesktop'

const AccommodationViewContainer = () => {
  const sapiMetaOptions = useSelector(getSapiMetaOptions)
  const hotelSlug = useHotelSlug()
  const hotelId = convertHotelSlugToHotelId(hotelSlug)
  const dataFile = useSelector(getDataFile)
  const variations = collectSapiVariationsParam(
    dataFile?.featureFlags.map(({key}) => key)
  )
  const dispatch = useDispatch()
  const {getQueryParams} = useRouterProvider()
  const getQueryParamsRef = useRef(getQueryParams)
  getQueryParamsRef.current = getQueryParams

  // useSyncFilters rewrites the filters in the url, so keep what the AP was opened with
  useEffect(() => {
    const {freeCancellation} = getQueryParamsRef.current()
    dispatch(setSrpEntryFilters({freeCancellation}))
    return () => {
      dispatch(clearSrpEntryFilters())
    }
  }, [dispatch])

  useSyncStoreWithUrl()
  useRedirectHome({hotelId})
  useSyncFilters()
  useGetHotelDetails(hotelId)

  const generateOptions = useCallback(
    () => ({
      ...sapiMetaOptions,
      pageSize: Number(process.env.NEXT_PUBLIC_SEARCH_PAGE_SIZE),
      variations
    }),
    [sapiMetaOptions, variations]
  )

  return (
    <TeamContextProvider team={Team.Select}>
      <TrackPageView pageName={Page.Accommodation} />
      <RoomsPreheatTracking />
      <InjectPageDataId page={Page.Accommodation} />

      <SapiProvider
        profileKey={process.env.NEXT_PUBLIC_SAPI_PROFILE_KEY as ProfileKey}
        generateOptions={generateOptions}
      >
        <OfferCheckContextProvider>
          <AccommodationView />
        </OfferCheckContextProvider>
      </SapiProvider>
    </TeamContextProvider>
  )
}

const AccommodationView = () => {
  const {isMobile, isDesktopMd, isDesktopLg} = useDeviceLayout()
  const {shell} = useBrandCapabilities()
  const searchBoxWithDestinationPicker = isDesktopMd || isDesktopLg
  // Collapsing-header brands (e.g. Kiwi) keep a sticky header that pulls its top
  // part out on scroll at every desktop width, like the SRP — not only when the
  // wider destination-picker search box is shown.
  const hasFixedHeaderOnDesktop =
    searchBoxWithDestinationPicker || shell.hasCollapsingHeader
  const hotelSlug = useHotelSlug()
  const hotelId = convertHotelSlugToHotelId(hotelSlug)
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))
  const {isComplete} = useAccommodationTopOffers({hotelId: hotel?.objectID})
  const [isFirstLoad, setIsFirstLoad] = React.useState(true)
  const dispatchTeamEvent = useDispatchTeamEvent()
  const dispatch = useDispatch()
  const isHydrated = useIsHydrated()
  const isSrpSearchSeoFlow = useIsSrpSearchSeoFlow()

  // The store survives going back to the SRP, so a later visit would reuse the pinned offer
  useEffect(
    () => () => {
      dispatch(clearRoomsTopOfferLock())
    },
    [dispatch]
  )

  // Inject SRP URL into browser history so back button navigates to SRP.
  // Safari-mobile is excluded inside the hook (it handles this natively).
  const srpQueryParams = useSrpQueryString(hotelSlug)
  const {changeLocation} = useUrlUpdater()
  useInjectSrpHistory(srpQueryParams, changeLocation)

  useTrackPriceRefreshTimeout({
    shouldSkip: false,
    delayMs: getAutoRefreshInterval(),
    page: Page.Accommodation
  })

  useConditionalEffect(
    async () =>
      import(
        /* webpackChunkName: "LanguageMismatchOverlay" */
        /* webpackPrefetch: true */
        'components/LanguageMismatchOverlay/LanguageMismatchOverlay'
      ),
    isSrpSearchSeoFlow
  )

  useEffect(() => {
    if (hotel && isFirstLoad && isComplete) {
      const hotelId = hotel.objectID
      setIsFirstLoad(false)
      dispatchTeamEvent(
        trackEvent({
          category: Category.System,
          entity: Entity.HotelDetails,
          action: Action.Displayed
        })
      )

      const {descriptions} = hotel
      const {version = 1} = descriptions || {}
      const hotelContext = {
        hotelId,
        isAIDescriptionEligible: version === 2
      } as HotelContext

      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetails,
          action: Action.Viewed,
          analyticsContext: {
            [AnalyticsContext.HotelContext]: hotelContext
          }
        })
      )

      dispatch(resetAccommodationPageState())
    }
  }, [hotel, isComplete, isFirstLoad, dispatch, dispatchTeamEvent])

  return (
    <>
      {isSrpSearchSeoFlow && <SeoAPStructuredData />}
      <RoomPriceStructuredData />
      <Layout
        hasHeaderFixedOnDesktop={hasFixedHeaderOnDesktop}
        hasHeaderOnMobile={false}
        hasFooterOnMobile={false}
        hasSearchForm={!isMobile}
        customSearchBox={<DesktopSearchBox />}
      >
        {isMobile ? <Mobile /> : <Desktop />}
        {isHydrated && <InitializeCallToBook hotelId={hotelId} />}
        {isSrpSearchSeoFlow && isHydrated && (
          <LanguageMismatchOverlay hotelId={hotelId} />
        )}
      </Layout>
    </>
  )
}

export default AccommodationViewContainer
